<script lang="ts" setup>
export interface PaginationProps {
    pagination: Pagination
    link: VNuxtLinkTo
}

const props = defineProps<PaginationProps>()
const firstAndLastPageCount = ref<number>(2)
const numberOfVisblePages = ref<number>(3)

const calcOffset = (): number => {
    let offset: number
    if (props.pagination.page === 2) {
        offset = 0
    } else if (props.pagination.page === props.pagination.last_page) {
        offset = numberOfVisblePages.value
    } else if (
        props.pagination.page >
        props.pagination.last_page - firstAndLastPageCount.value
    ) {
        offset = firstAndLastPageCount.value
    } else {
        offset = 1
    }
    return Math.max(2, props.pagination.page - offset)
}

// create array first pages [1,2,3] or less
const pagesGroup = computed<number[]>((): number[] => {
    const numberOfpagesBetweenFirstAndLast = Math.max(
        0,
        props.pagination.last_page - firstAndLastPageCount.value
    )

    // if last page <= 5 show first and last + all pages between
    if (numberOfpagesBetweenFirstAndLast <= 3) {
        return Array(numberOfpagesBetweenFirstAndLast)
            .fill(0)
            .map((_, index) => index + firstAndLastPageCount.value)
    }

    // last page is > 5, show first and last + 3 pages between depending and page position
    const offset: number = calcOffset()
    return Array(3)
        .fill(0)
        .map((_, index) => index + offset)
})
</script>

<template>
  <nav
    v-if="props.pagination.last_page > 1"
    class="d-md-flex justify-content-md-start mt-4"
  >
    <Tracker :data="{ position: 'Pagination' }">
      <ul
        class="d-flex justify-content-center justify-content-md-between"
      >
        <PaginationLink
          v-if="props.pagination.page > 1"
          :is-active="false"
          :link="props.link"
          :page="props.pagination.page - 1"
          :pagination="props.pagination"
          :aria-label="$t('Predchádzajúca stránka')"
        >
          <svg
            class="d-block"
            width="18"
            height="8"
            aria-hidden="true"
          >
            <use
              xlink:href="#arrow-left"
            />
          </svg>
        </PaginationLink>
        <PaginationLink
          :is-active="props.pagination.page === 1"
          :link="props.link"
          :page="1"
          :pagination="props.pagination"
        >
          1
        </PaginationLink>
        <PaginationDots
          v-if="
            props.pagination.page > 3 &&
              props.pagination.last_page > 5
          "
        />
        <PaginationLink
          v-for="(item, index) in pagesGroup"
          :key="index"
          :is-active="item === props.pagination.page"
          :link="props.link"
          :page="item"
          :pagination="props.pagination"
        >
          {{ item }}
        </PaginationLink>
        <PaginationDots
          v-if="
            props.pagination.page <=
              props.pagination.last_page - numberOfVisblePages &&
              props.pagination.last_page > 5
          "
        />
        <PaginationLink
          :is-active="
            props.pagination.last_page === props.pagination.page
          "
          :link="props.link"
          :page="props.pagination.last_page"
          :pagination="props.pagination"
        >
          {{ props.pagination.last_page }}
        </PaginationLink>
        <PaginationLink
          v-if="props.pagination.page < props.pagination.last_page"
          :is-active="false"
          :link="props.link"
          :page="props.pagination.page + 1"
          :pagination="props.pagination"
          :aria-label="$t('Ďalšia stránka')"
        >
          <svg
            class="d-block"
            width="18"
            height="8"
            aria-hidden="true"
          >
            <use xlink:href="#arrow-right" />
          </svg>
        </PaginationLink>
      </ul>
      <IconsWrapper>
        <IconsSymbolArrowLeft />
        <IconsSymbolArrowRight />
      </IconsWrapper>
    </Tracker>
  </nav>
</template>
